import { DraggableItem } from '@bringg-frontend/draggable-list-selector';
import { getRoot } from 'mobx-easy';
import { debounce } from 'lodash';
import { ColumnDataBasic, DetailedColumn, ViewColumn, panelSelector } from '@bringg-frontend/monitoring-shared';

import { ColumnConfigItem } from '../details-table/hooks/use-columns';
import RootStore from 'bringg-web/stores/root-store';
import { FULL_SCREEN } from './resizable-panels';

const spreadColumnsEven = (numberOfColumns: number): number[] => {
	const evenSize = FULL_SCREEN / numberOfColumns;

	return Array.from({ length: numberOfColumns }, () => evenSize).reduce((sizes, _, index) => {
		if (index === numberOfColumns - 1) {
			const sum = sizes.reduce((_sum, size) => _sum + size, 0);
			const lastSize = FULL_SCREEN - sum;
			sizes.push(lastSize);
		} else {
			sizes.push(evenSize);
		}

		return sizes;
	}, []);
};

const addColumn = (
	groupId: string,
	columns: ColumnConfigItem[],
	layoutMap: Map<string, number>,
	spreadEven: boolean
) => {
	const panelGroupEl: HTMLElement = document.querySelector(panelSelector(groupId));
	const panelWidth = panelGroupEl.offsetWidth;
	const newColumnWidth = FULL_SCREEN * (columns.length - layoutMap.size);
	const changeRatio = (panelWidth + newColumnWidth) / panelWidth;
	let sizes = [];

	if (spreadEven) {
		sizes = spreadColumnsEven(columns.length);
	} else {
		const index = columns.findIndex(column => !layoutMap.has(column.id));
		sizes = columns.reduce((_sizes, column) => {
			layoutMap.has(column.id) &&
				_sizes.push(Math.round((layoutMap.get(column.id) * panelWidth) / (panelWidth + newColumnWidth)));
			return _sizes;
		}, []);

		const sum = sizes.reduce((_sum, size) => _sum + size, 0);
		const newColumnSize = FULL_SCREEN - sum;

		sizes.splice(index, 0, newColumnSize);
	}

	return { sizes, changeRatio };
};

const removeColumn = (
	groupId: string,
	columns: ColumnConfigItem[],
	layoutMap: Map<string, number>,
	spreadEven: boolean
) => {
	const panelGroupEl: HTMLElement = document.querySelector(panelSelector(groupId));
	const panelWidth = panelGroupEl.offsetWidth;

	const removedLayoutItems = [...layoutMap].filter(([id]) => !columns.find(_column => _column.id === id));
	const removedColumnSize = removedLayoutItems.reduce((_sum, [_, size]) => {
		return _sum + size;
	}, 0);

	const removedColumnWidth = (removedColumnSize / FULL_SCREEN) * panelWidth;
	const changeRatio = (panelWidth - removedColumnWidth) / panelWidth || 1;
	let sizes: number[] = [];

	if (spreadEven) {
		sizes = spreadColumnsEven(columns.length);
	} else {
		sizes = columns.reduce((_sizes, column, index) => {
			if (index === columns.length - 1) {
				// for making sure sizes are not > 100 (due to decimal precision => 100.00000000001)
				const sum = _sizes.reduce((_sum, size) => _sum + size, 0);
				const lastSize = FULL_SCREEN - sum;
				_sizes.push(lastSize > 0 ? lastSize : 0);
			} else {
				_sizes.push(Math.round(layoutMap.get(column.id) * (1 - changeRatio + 1)));
			}
			return _sizes;
		}, []);
	}

	return { sizes, changeRatio };
};

const reorderColumns = (columns: ColumnConfigItem[], layoutMap: Map<string, number>, spreadEven: boolean) => {
	let sizes = [];

	if (spreadEven) {
		sizes = spreadColumnsEven(columns.length);
	} else {
		sizes = columns.reduce((_sizes, column, index) => {
			if (index === columns.length - 1) {
				// for making sure sizes are not > 100 (due to decimal precision => 100.00000000001)
				const sum = _sizes.reduce((_sum, size) => _sum + size, 0);
				const lastSize = FULL_SCREEN - sum;
				_sizes.push(lastSize > 0 ? lastSize : 0);
			} else {
				_sizes.push(layoutMap.get(column.id));
			}
			return _sizes;
		}, []);
	}

	return sizes;
};

const userLayout: Map<string, number[]> = new Map();
const getUserColumnsSizes = (keyPrefix: string) => {
	if (!userLayout.has(keyPrefix)) {
		const { id } = getRoot<RootStore>().data.usersStore.currentUser;
		userLayout.set(keyPrefix, (JSON.parse(localStorage.getItem(`${keyPrefix}-${id}`)) as number[]) || []);
	}

	return userLayout.get(keyPrefix);
};

const saveUserColumnSizes = debounce((keyPrefix: string, sizes: number[]) => {
	const validatedSizes = sizes.reduce((_sizes, size, index) => {
		if (index === sizes.length - 1) {
			// for making sure sizes are not > 100 (due to decimal precision => 100.00000000001)
			const sum = _sizes.reduce((_sum, _size) => _sum + _size, 0);
			const lastSize = FULL_SCREEN - sum;

			_sizes.push(lastSize > 0 ? lastSize : 0);
		} else {
			_sizes.push(size);
		}

		return _sizes;
	}, [] as number[]);

	const { id } = getRoot<RootStore>().data.usersStore.currentUser;
	localStorage.setItem(`${keyPrefix}-${id}`, JSON.stringify(validatedSizes));
}, 1000);

const calculateVisibleColumns = <T extends ColumnDataBasic, G extends ColumnDataBasic | void>(
	groupId: string,
	columns: ColumnConfigItem[],
	availableColumns: DetailedColumn<T, G>[],
	layoutMap: Map<string, number>,
	spreadEven: boolean,
	keyPrefix: string
): { columns: ViewColumn<T, G>[]; changeRatio: number } => {
	let sizes = getUserColumnsSizes(keyPrefix),
		changeRatio = 1;

	if (sizes.length !== columns.length) {
		sizes = [];
	}

	if (layoutMap.size > 0) {
		if (columns.length > layoutMap.size) {
			({ sizes, changeRatio } = addColumn(groupId, columns, layoutMap, spreadEven));
		}

		if (columns.length < layoutMap.size) {
			({ sizes, changeRatio } = removeColumn(groupId, columns, layoutMap, spreadEven));
		}

		if (layoutMap.size === columns.length) {
			sizes = reorderColumns(columns, layoutMap, spreadEven);
		}
	}

	return {
		columns: columns
			.map(({ id }, index) => {
				const viewColumn = availableColumns.find(column => column.id === id) as ViewColumn<T, G>;
				if (viewColumn) viewColumn.size = sizes[index];
				return viewColumn;
			})
			// if from some reason availableColumns do not contain that column
			.filter(Boolean),
		changeRatio
	};
};

const calculateColumnConfig = <T extends ColumnDataBasic, G extends ColumnDataBasic | void>(
	availableColumns: DetailedColumn<T, G>[],
	selectedColumns: ColumnConfigItem[]
) => {
	const config = availableColumns.reduce(
		(columnsToSet, column) => {
			const index = selectedColumns.findIndex(({ id }) => id === column.id);
			if (index > -1) {
				columnsToSet.selectedValues[index] = {
					id: column.id,
					title: column.Header as string,
					required: selectedColumns[index].required
				};
			} else {
				columnsToSet.unselectedValues.push({ id: column.id, title: column.Header as string });
			}

			return columnsToSet;
		},
		{ selectedValues: [], unselectedValues: [] } as {
			selectedValues: DraggableItem[];
			unselectedValues: DraggableItem[];
		}
	);

	return {
		selectedValues: config.selectedValues.filter(Boolean),
		unselectedValues: config.unselectedValues
	};
};

const getUserColumnConfig = (keyPrefix: string): ColumnConfigItem[] | null => {
	const { id } = getRoot<RootStore>().data.usersStore.currentUser;
	const userColumnConfig = localStorage.getItem(`${keyPrefix}-${id}`);

	if (userColumnConfig) {
		return JSON.parse(userColumnConfig) as ColumnConfigItem[];
	}

	return null;
};

const saveUserColumnConfig = (keyPrefix: string, selectedConfig: ColumnConfigItem[]) => {
	const { id } = getRoot<RootStore>().data.usersStore.currentUser;
	localStorage.setItem(`${keyPrefix}-${id}`, JSON.stringify(selectedConfig));
};

const clearUserColumnConfig = (keyPrefix: string) => {
	const { id } = getRoot<RootStore>().data.usersStore.currentUser;
	localStorage.removeItem(`${keyPrefix}-${id}`);
};

export const columnsProvider = {
	calculateColumnConfig,
	calculateVisibleColumns,
	getUserColumnConfig,
	saveUserColumnConfig,
	clearUserColumnConfig,
	saveUserColumnSizes
};
